import {
  defaultJobListFilter,
  type JobListFilter,
  jobListFilterQueryParams,
} from '@features/jobs';
import apiProtected from '@lib/api/apiClient';
import { asList, getBody, server } from '@lib/api/http';
import { queryKeys } from '@lib/api/queryKeys';
import { useSession } from '@lib/auth/session';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';

export const BUYER_JOBS_PAGE_SIZE = 20;

type BuyerJobsPage = {
  data?: unknown[];
  totalCount?: number;
};

const nextBuyerJobsPage = (
  lastPage: BuyerJobsPage,
  allPages: BuyerJobsPage[]
) => {
  if ((lastPage.data?.length ?? 0) < BUYER_JOBS_PAGE_SIZE) {
    return undefined;
  }
  const loaded = allPages.reduce(
    (count, page) => count + (page.data?.length ?? 0),
    0
  );
  const totalCount = lastPage.totalCount;
  if (totalCount != null && totalCount > 0 && loaded >= totalCount) {
    return undefined;
  }
  return allPages.length;
};

const useBuyerJobsInfiniteQuery = (
  queryKey: readonly string[],
  path: string,
  search = '',
  filter: JobListFilter = defaultJobListFilter
) => {
  const loggedIn = useSession();
  const trimmed = search.trim();
  const filterParams = jobListFilterQueryParams(filter);
  const query = useInfiniteQuery({
    queryKey: [...queryKey, trimmed, filterParams],
    queryFn: async ({ pageParam }) =>
      getBody<BuyerJobsPage>(`${server}${path}`, {
        page: pageParam,
        limit: BUYER_JOBS_PAGE_SIZE,
        ...(trimmed ? { search: trimmed } : {}),
        ...filterParams,
      }),
    initialPageParam: 0,
    getNextPageParam: nextBuyerJobsPage,
    enabled: loggedIn,
    placeholderData: keepPreviousData,
  });

  const jobs = useMemo(
    () => query.data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [query.data]
  );

  return { ...query, jobs };
};

export const useBuyerCurrentJobsQuery = (
  search = '',
  filter: JobListFilter = defaultJobListFilter
) =>
  useBuyerJobsInfiniteQuery(
    queryKeys.buyerCurrentJobs,
    'v1/jobs/get-current-jobs-for-buyer',
    search,
    filter
  );

export const useBuyerPastJobsQuery = (
  search = '',
  filter: JobListFilter = defaultJobListFilter
) =>
  useBuyerJobsInfiniteQuery(
    queryKeys.buyerPastJobs,
    'v1/jobs/get-past-jobs-for-buyer',
    search,
    filter
  );

export type R8trJobsListQueryOptions = {
  search?: string;
  sort?: 'distance' | 'pay' | 'value';
};

const r8trJobsListParams = (
  filter: JobListFilter,
  options: R8trJobsListQueryOptions = {}
) => {
  const trimmed = options.search?.trim() ?? '';
  return {
    ...jobListFilterQueryParams(filter),
    ...(trimmed ? { search: trimmed } : {}),
    ...(options.sort ? { sort: options.sort } : {}),
  };
};

export const useAvailableJobsQuery = (
  filter: JobListFilter = defaultJobListFilter,
  options: R8trJobsListQueryOptions = {}
) => {
  const loggedIn = useSession();
  const params = r8trJobsListParams(filter, options);
  return useQuery({
    queryKey: [...queryKeys.availableJobs, params],
    queryFn: async () =>
      asList(
        await getBody<{ data?: unknown[] }>(
          `${server}v1/jobs/get-available-jobs`,
          params
        )
      ),
    enabled: loggedIn,
    placeholderData: keepPreviousData,
  });
};

export const useCurrentJobsQuery = (
  filter: JobListFilter = defaultJobListFilter,
  options: R8trJobsListQueryOptions = {}
) => {
  const loggedIn = useSession();
  const params = r8trJobsListParams(filter, options);
  return useQuery({
    queryKey: [...queryKeys.currentJobs, params],
    queryFn: async () =>
      asList(
        await getBody<{ data?: unknown[] }>(
          `${server}v1/jobs/get-current-jobs-for-r8tr`,
          params
        )
      ),
    enabled: loggedIn,
    placeholderData: keepPreviousData,
  });
};

export const useJobDetailQuery = (jobId?: string) => {
  const loggedIn = useSession();
  return useQuery<any>({
    queryKey: queryKeys.jobDetail(jobId),
    queryFn: async () => {
      const body = await getBody<{ data?: unknown }>(
        `${server}v1/jobs/get-job-details`,
        { id: jobId }
      );
      return body.data;
    },
    enabled: loggedIn && Boolean(jobId),
  });
};

export const useJobPdfQuery = (jobId?: string | number) => {
  const loggedIn = useSession();
  return useQuery<any>({
    queryKey: queryKeys.jobPdf(jobId),
    queryFn: async () => {
      const body = await getBody<{ data?: unknown }>(
        `${server}v1/jobs/get-job-details-for-pdf-for-non-company-jobs`,
        { id: jobId }
      );
      return body.data;
    },
    enabled: loggedIn && jobId != null,
  });
};

export const useScrappedItemQuery = (itemId?: string | null) => {
  const loggedIn = useSession();
  return useQuery<any>({
    queryKey: queryKeys.scrappedItem(itemId),
    queryFn: async () => {
      const body = await getBody<{ data?: unknown }>(
        `${server}v1/scrap/scrapped-item-details`,
        { itemId }
      );
      return body.data ?? {};
    },
    enabled: loggedIn && Boolean(itemId),
  });
};

export const useAcceptJobMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) => {
      const { data } = await apiProtected.patch(
        `${server}v1/jobs/job-accepted-by-r8tr`,
        {},
        { params: { jobId } }
      );
      return data?.message as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
};

export const useCancelJobMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) => {
      const { data } = await apiProtected.patch(
        `${server}v1/jobs/job-cancelled-by-r8tr`,
        {},
        { params: { jobId } }
      );
      return data?.message as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
};

export const useCompleteJobMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string | number) =>
      apiProtected.patch(
        `${server}v1/jobs/job-completed-by-r8tr`,
        {},
        { params: { jobId } }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
};

export const useOnMyWayMutation = () => {
  return useMutation({
    mutationFn: (jobId?: string) =>
      apiProtected.patch(
        `${server}v1/jobs/on-my-way`,
        {},
        { params: { jobId } }
      ),
  });
};

export const useCancelJobByBuyerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { jobId?: string; reason: string }) =>
      apiProtected.patch(
        `${server}v1/jobs/cancel-job-by-buyer`,
        { deletionOrCancellationReason: input.reason },
        { params: { jobId: input.jobId } }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.buyerCurrentJobs });
    },
  });
};

export const useAddTipMutation = () => {
  return useMutation({
    mutationFn: (input: {
      tip: number | string;
      jobId: string | number;
      nonce: string;
    }) => apiProtected.patch(`${server}v1/jobs/add-tip`, input),
  });
};

export const useCreateJobMutation = () => {
  return useMutation({
    mutationFn: (body: object) => apiProtected.post(`${server}v1/jobs/`, body),
  });
};
